import * as React from 'react';
import { useEffect, useState } from 'react';
import CSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';

import keysToConstants from 'js/lib/keysToConstants';

import { previewPrePurchaseUpsellVariant } from 'bundles/coursera-plus-common/utils/prePurchaseUpsellExperimentUtils';
import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import {
  generateImpressionForShowNewXdpValueProps,
  isShowNewXdpValuePropsEnabled,
} from 'bundles/enroll-common/utils/experimentUtils';
import { useEnrollInS12nSubscription } from 'bundles/enroll-common/utils/useEnrollInS12nSubscription';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import PromotionApplicableCheckoutMessage from 'bundles/enroll-modal/components/common/PromotionApplicableCheckoutMessage';
import CourseraPlusPrePurchaseUpsellModal from 'bundles/enroll-modal/components/coursera-plus/prePurchaseUpsell/CourseraPlusPrePurchaseUpsellModal';
import SubscriptionEnrollButton from 'bundles/enroll-modal/components/subscriptions/SubscriptionEnrollButton';
import SubscriptionFooter from 'bundles/enroll-modal/components/subscriptions/SubscriptionFooter';
import SubscriptionPriceHeader from 'bundles/enroll-modal/components/subscriptions/SubscriptionPriceHeader';
import SubscriptionVProp from 'bundles/enroll-modal/components/subscriptions/SubscriptionVProp';
import SubscriptionVPropV2 from 'bundles/enroll-modal/components/subscriptions/SubscriptionVPropV2';
import SubscriptionVPropFreeTrial from 'bundles/enroll-modal/components/subscriptions/free-trialV2/SubscriptionVPropFreeTrial';
import usePageData from 'bundles/enroll-modal/data/usePageData';
import { useEnrollModalEventing } from 'bundles/enroll-modal/utils/eventingUtils';
import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/enroll';

import 'css!./__styles__/SubscriptionEnrollModal';

const MODAL_TYPES = keysToConstants(['ENROLL', 'ERROR', 'PRE_PURCHASE_UPSELL']);

type PropsFromCaller = {
  onClose: () => void;
  onSdp: boolean;
  isFromS12nSelection?: boolean;
  isPrePurchaseUpsellEligible?: boolean;
};

const SubscriptionEnrollModal = ({
  onClose,
  onSdp,
  isFromS12nSelection = false,
  isPrePurchaseUpsellEligible,
}: PropsFromCaller) => {
  const [activeModal, setActiveModal] = useState<keyof typeof MODAL_TYPES | null>(MODAL_TYPES.ENROLL);
  const [error, setError] = useState<ApiError | undefined>();
  const [enrolling, setEnrolling] = useState(false);
  const { getEnrollModalTrackingRef, trackEnrollModalClose } = useEnrollModalEventing();
  const visibilityTrackingRef = getEnrollModalTrackingRef();
  const showNewValueProps = isShowNewXdpValuePropsEnabled();
  const isPrePurchaseUpsellEnabled =
    isPrePurchaseUpsellEligible && ['1', '2'].includes(previewPrePurchaseUpsellVariant());
  const { s12n, course } = usePageData();
  const {
    s12nMonthlySkuId,
    canEnrollThroughS12nSubscription,
    canEnrollThroughS12nSubscriptionFreeTrial,
    enrollInSubscription,
  } = useEnrollInS12nSubscription(isFromS12nSelection);

  useEffect(() => {
    if (canEnrollThroughS12nSubscription) {
      generateImpressionForShowNewXdpValueProps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!s12n || !s12nMonthlySkuId) {
    return null;
  }

  const handleClose = () => {
    trackEnrollModalClose();
    onClose();
  };

  const handleError = (apiError: ApiError) => {
    setEnrolling(false);
    setActiveModal(MODAL_TYPES.ERROR);
    setError(apiError);
  };

  const proceedToCheckout = () => {
    setEnrolling(true);
    enrollInSubscription().catch(handleError);
  };

  const onClickSubscribeCta = () => {
    if (isPrePurchaseUpsellEnabled) {
      setActiveModal(MODAL_TYPES.PRE_PURCHASE_UPSELL);
    } else {
      proceedToCheckout();
    }
  };

  const handlePrePurchaseUpsellContinue = () => {
    // TODO: Implement the actual Coursera Plus purchase flow
    // For now, close the modal
    handleClose();
  };

  const onDeclinePrePurchaseUpsell = (shouldGoToCheckoutOnClose: boolean) => {
    if (shouldGoToCheckoutOnClose) {
      proceedToCheckout();
    } else {
      handleClose();
    }
  };

  const renderHeader = (): React.ReactNode => {
    if (canEnrollThroughS12nSubscription) {
      return <SubscriptionPriceHeader />;
    } else {
      return null;
    }
  };

  const renderBody = (): React.ReactNode => {
    if (canEnrollThroughS12nSubscriptionFreeTrial) {
      return <SubscriptionVPropFreeTrial onSdp={onSdp} />;
    } else if (canEnrollThroughS12nSubscription) {
      if (showNewValueProps) {
        return <SubscriptionVPropV2 onSdp={onSdp} />;
      } else {
        return <SubscriptionVProp onSdp={onSdp} />;
      }
    } else {
      return null;
    }
  };

  switch (activeModal) {
    case MODAL_TYPES.PRE_PURCHASE_UPSELL:
      return (
        <CourseraPlusPrePurchaseUpsellModal
          onDeclinePrePurchaseUpsell={onDeclinePrePurchaseUpsell}
          onContinue={handlePrePurchaseUpsellContinue}
          isS12n={true}
        />
      );
    case MODAL_TYPES.ENROLL:
      return (
        <div className="rc-SubscriptionEnrollModal" data-testid="subscription-enroll-modal">
          <CSSTransitionGroup transitionName="fade" transitionEnter={false} transitionLeaveTimeout={300}>
            <Modal
              trackingName="subscription_enroll_modal"
              key="SubscriptionEnrollModal"
              modalName={_t('Join this Specialization')}
              handleClose={handleClose}
              className="subscription-enroll-modal"
            >
              <div className="enroll-modal-container" ref={visibilityTrackingRef}>
                {isFromS12nSelection && (
                  <div className="m-t-1s">
                    <strong data-unit="step2">{_t('Step 2 of 2')}</strong>
                  </div>
                )}
                {renderHeader()}
                <PromotionApplicableCheckoutMessage
                  course={course}
                  s12n={s12n}
                  isFromS12nSelection={isFromS12nSelection}
                />
                {renderBody()}
                <SubscriptionEnrollButton
                  isFromS12nSelection={isFromS12nSelection}
                  onClick={onClickSubscribeCta}
                  enrolling={enrolling}
                />
                {!onSdp && <SubscriptionFooter />}
              </div>
            </Modal>
          </CSSTransitionGroup>
        </div>
      );
    case MODAL_TYPES.ERROR:
      return <EnrollErrorModal error={error} onClose={handleClose} isFinancialAid={false} isFreeEnroll={false} />;
    default:
      return null;
  }
};

export default SubscriptionEnrollModal;
